/**
 * @module composables/useDashboardLayout
 * @description 대시보드 위젯 레이아웃(가시성/순서/접힘)을 LocalStorage에 영속화하고,
 * 권한 기반 필터링과 모바일 정렬 모드를 통합 관리하는 composable. (SURINP-183 v2 ADDENDUM)
 *
 * 책임:
 * - 사용자별 LocalStorage 키(`cs-dashboard-layout:{userId}`)로 visibility/order/collapsed 영속화
 * - schema version 검증 — 구버전/깨진 양식은 default로 안전 폴백
 * - useDashboardPermission과 통합하여 adminOnly 위젯 자동 제외 (visibleWidgets)
 * - ADMIN 전용 프리셋 적용(applyPreset) 및 전체 리셋(resetLayout)
 * - 모바일 long-press 정렬 모드(mobileSortMode) 상태 제공
 *
 * 주의:
 * - 백엔드 sync 미사용 (D5-2). 다중 디바이스 sync는 미지원
 * - LocalStorage 양식 깨짐(JSON.parse 실패, version 불일치, items 비배열) 시 default로 폴백
 * - reorder는 입력 배열의 id만 활용. visible/collapsed는 기존 값 보존
 */
import { ref, computed, watch } from 'vue'
import store from '@/store'
import { useDashboardPermission } from '@/composables/useDashboardPermission'
import {
  DASHBOARD_WIDGETS,
  WIDGET_BY_ID,
  DASHBOARD_PRESETS,
  DASHBOARD_LAYOUT_SCHEMA_VERSION,
  DASHBOARD_LAYOUT_STORAGE_PREFIX
} from '@/constants/dashboard-widgets'

/** 비로그인 / 식별자 미확보 시 사용할 폴백 사용자 키. */
const FALLBACK_USER_KEY = 'guest'

/**
 * Vuex 인증 상태에서 현재 사용자의 안정적인 식별자를 추출한다.
 * 우선순위: state.userName → state.auth.user.id.id → state.auth.user.id.username → 폴백
 *
 * @returns {string} LocalStorage 키 suffix로 사용할 사용자 식별자
 */
function readUserKey() {
  const auth = store?.state?.auth
  if (!auth) {
    return FALLBACK_USER_KEY
  }
  if (typeof auth.userName === 'string' && auth.userName.length > 0) {
    return auth.userName
  }
  const id = auth.user?.id
  if (id && typeof id === 'object') {
    if (typeof id.id === 'string' && id.id.length > 0) return id.id
    if (typeof id.username === 'string' && id.username.length > 0) return id.username
  }
  return FALLBACK_USER_KEY
}

/**
 * 위젯 정의(constants)로부터 default 레이아웃 항목을 도출한다.
 *
 * @returns {Array<{id: string, visible: boolean, collapsed: boolean, order: number}>}
 */
function buildDefaultLayout() {
  return DASHBOARD_WIDGETS.map((widget) => ({
    id: widget.id,
    visible: widget.defaultVisible,
    collapsed: false,
    order: widget.defaultOrder
  }))
}

/**
 * LocalStorage에서 저장된 레이아웃을 안전하게 읽어온다.
 * 파싱 실패, version 불일치, 형태 이상 시 null 반환 → 호출자가 default로 폴백.
 *
 * @param {string} storageKey
 * @returns {Array<{id: string, visible: boolean, collapsed: boolean, order: number}>|null}
 */
function readStorage(storageKey) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.version !== DASHBOARD_LAYOUT_SCHEMA_VERSION) return null
    if (!Array.isArray(parsed.items)) return null
    const known = parsed.items
      .filter((item) => item && typeof item.id === 'string' && WIDGET_BY_ID[item.id])
      .map((item) => ({
        id: item.id,
        visible: item.visible !== false,
        collapsed: item.collapsed === true,
        order: Number.isFinite(item.order) ? Number(item.order) : WIDGET_BY_ID[item.id].defaultOrder
      }))
    if (known.length === 0) return null
    // 신규 위젯이 추가된 경우 default로 채워 넣어 누락 방지
    const knownIds = new Set(known.map((item) => item.id))
    const merged = [...known]
    for (const widget of DASHBOARD_WIDGETS) {
      if (!knownIds.has(widget.id)) {
        merged.push({
          id: widget.id,
          visible: widget.defaultVisible,
          collapsed: false,
          order: widget.defaultOrder
        })
      }
    }
    return merged
  } catch (e) {
    if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
      window.__APP_LOG__(e)
    }
    return null
  }
}

/**
 * LocalStorage에 레이아웃을 안전하게 저장한다.
 * write 실패(quota exceeded 등)는 조용히 무시 — UX는 메모리 내 상태 유지로 진행.
 *
 * @param {string} storageKey
 * @param {Array<{id: string, visible: boolean, collapsed: boolean, order: number}>} layout
 * @returns {void}
 */
function writeStorage(storageKey, layout) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    const payload = {
      version: DASHBOARD_LAYOUT_SCHEMA_VERSION,
      items: layout
    }
    window.localStorage.setItem(storageKey, JSON.stringify(payload))
  } catch (e) {
    if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
      window.__APP_LOG__(e)
    }
  }
}

/**
 * 대시보드 위젯 레이아웃 composable.
 *
 * @returns {{
 *   layout: import('vue').Ref<Array<{id: string, visible: boolean, collapsed: boolean, order: number}>>,
 *   visibleWidgets: import('vue').ComputedRef<Array<object>>,
 *   mobileSortMode: import('vue').Ref<boolean>,
 *   isCollapsed: (id: string) => boolean,
 *   toggleVisible: (id: string) => void,
 *   toggleCollapse: (id: string) => void,
 *   reorder: (newList: Array<{id: string}>) => void,
 *   applyPreset: (presetId: string) => boolean,
 *   resetLayout: () => void,
 *   enterMobileSortMode: () => void,
 *   exitMobileSortMode: () => void,
 *   storageKey: import('vue').ComputedRef<string>
 * }}
 */
export function useDashboardLayout() {
  const { filterByPermission } = useDashboardPermission()

  /** 현재 사용자별 LocalStorage 키 (computed로 사용자 변경 추적) */
  const storageKey = computed(() => `${DASHBOARD_LAYOUT_STORAGE_PREFIX}${readUserKey()}`)

  /** 위젯 레이아웃 상태 — 초기값은 LocalStorage 우선, 없으면 default */
  const layout = ref(readStorage(storageKey.value) ?? buildDefaultLayout())

  /** 모바일 long-press 정렬 모드 (드래그 비활성 + 화살표 노출 진입 플래그) */
  const mobileSortMode = ref(false)

  /**
   * 권한 기반으로 필터링하고 order 오름차순 정렬한 위젯 목록.
   * AdminDashBoardView가 vuedraggable의 v-model로 사용한다.
   */
  const visibleWidgets = computed(() => {
    const items = layout.value
      .filter((item) => item.visible && WIDGET_BY_ID[item.id])
      .map((item) => ({ ...WIDGET_BY_ID[item.id], ...item }))
    const permitted = filterByPermission(items)
    return [...permitted].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order
      // 동일 order는 layout 좌→우 순으로
      const layoutOrder = { 'half-left': 0, full: 1, 'half-right': 2 }
      return (layoutOrder[a.layout] ?? 99) - (layoutOrder[b.layout] ?? 99)
    })
  })

  /**
   * 특정 위젯의 접힘 상태를 조회한다.
   *
   * @param {string} id
   * @returns {boolean}
   */
  function isCollapsed(id) {
    const item = layout.value.find((entry) => entry.id === id)
    return item ? item.collapsed === true : false
  }

  /**
   * 특정 위젯의 visibility를 토글한다.
   *
   * @param {string} id
   * @returns {void}
   */
  function toggleVisible(id) {
    layout.value = layout.value.map((item) =>
      item.id === id ? { ...item, visible: !item.visible } : item
    )
  }

  /**
   * 특정 위젯의 접힘 상태를 토글한다.
   *
   * @param {string} id
   * @returns {void}
   */
  function toggleCollapse(id) {
    layout.value = layout.value.map((item) =>
      item.id === id ? { ...item, collapsed: !item.collapsed } : item
    )
  }

  /**
   * vuedraggable이 새 순서를 반영한 배열을 전달하면 order 필드를 1-based로 재할당한다.
   * visible/collapsed는 기존 값을 그대로 보존한다.
   * 입력 배열에 없는 위젯은 끝쪽 order로 보존된다 (정의 누락 방지).
   *
   * @param {Array<{id: string}>} newList
   * @returns {void}
   */
  function reorder(newList) {
    if (!Array.isArray(newList)) return
    const orderMap = Object.create(null)
    newList.forEach((item, index) => {
      if (item && typeof item.id === 'string') {
        orderMap[item.id] = index + 1
      }
    })
    const maxOrder = newList.length
    layout.value = layout.value.map((item) => {
      const next = Object.prototype.hasOwnProperty.call(orderMap, item.id)
        ? orderMap[item.id]
        : maxOrder + 1
      return { ...item, order: next }
    })
  }

  /**
   * 프리셋을 적용한다. 프리셋의 visibleIds에 포함된 위젯만 visible=true,
   * 나머지는 visible=false로 설정. order는 정의된 defaultOrder 기준.
   *
   * v4부터 일반 사용자도 호출 가능. adminOnly 위젯이 visibleIds에 포함되어 있어도
   * useDashboardPermission이 visibleWidgets 단계에서 한 번 더 필터하므로
   * 일반 유저에게는 노출되지 않는다.
   *
   * - 존재하지 않는 presetId는 거부 (false 반환)
   *
   * @param {string} presetId
   * @returns {boolean} 적용 성공 여부
   */
  function applyPreset(presetId) {
    const preset = DASHBOARD_PRESETS.find((p) => p.id === presetId)
    if (!preset) return false
    const visibleSet = new Set(preset.visibleIds)
    layout.value = DASHBOARD_WIDGETS.map((widget) => ({
      id: widget.id,
      visible: visibleSet.has(widget.id),
      collapsed: false,
      order: widget.defaultOrder
    }))
    return true
  }

  /**
   * 레이아웃을 default로 초기화한다. LocalStorage도 즉시 갱신된다.
   *
   * @returns {void}
   */
  function resetLayout() {
    layout.value = buildDefaultLayout()
  }

  /** 모바일 정렬 모드 진입 (long-press 핸들러에서 호출) */
  function enterMobileSortMode() {
    mobileSortMode.value = true
  }

  /** 모바일 정렬 모드 종료 (정렬 완료 / 외부 클릭 시 호출) */
  function exitMobileSortMode() {
    mobileSortMode.value = false
  }

  // ── LocalStorage 영속화 ──
  // layout이 변경될 때마다 즉시 저장. user 전환이 발생하면 storageKey가 바뀌고
  // layout도 재로드해야 한다 — storageKey watch에서 처리.
  // 사용자 전환 시 readStorage→layout 재할당이 layout watch를 발동시켜
  // 같은 키에 동일 데이터를 즉시 다시 쓰는 이중 쓰기를 차단하기 위해 가드 플래그를 둔다.
  let isSwitchingUser = false

  watch(
    layout,
    (next) => {
      if (isSwitchingUser) return
      writeStorage(storageKey.value, next)
    },
    { deep: true }
  )

  watch(storageKey, (key) => {
    isSwitchingUser = true
    layout.value = readStorage(key) ?? buildDefaultLayout()
    // layout watch는 microtask로 flush되므로 nextTick 이후에 가드 해제
    Promise.resolve().then(() => {
      isSwitchingUser = false
    })
  })

  return {
    layout,
    visibleWidgets,
    mobileSortMode,
    isCollapsed,
    toggleVisible,
    toggleCollapse,
    reorder,
    applyPreset,
    resetLayout,
    enterMobileSortMode,
    exitMobileSortMode,
    storageKey
  }
}
